import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { collectionPath } from './firestoreCollections';

/**
 * Notification Levels:
 * - 'info': General information (shows in Recent only)
 * - 'success': Positive confirmations like import/export completion (shows in Recent only)
 * - 'warning': Needs attention - deadlines, extensions (shows in Important)
 * - 'urgent': Critical items requiring immediate action (shows in Important)
 */

// Generate user-friendly notification ID
function generateNotificationId(level, type = 'general') {
  const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0];
  const levelPrefix = level.substring(0, 3).toUpperCase(); // INFO, SUC, WAR, URG
  const typePrefix = type.substring(0, 3).toUpperCase(); // GEN, OPP, TSK, IMP, EXP
  return `${levelPrefix}_${typePrefix}_${timestamp}`;
}

// Create a persistent notification in Firestore
export async function createNotification({ title, body, level = 'info', recipients = [], related = null }) {
  const notificationType = related?.type || 'general';
  const notificationId = generateNotificationId(level, notificationType);
  
  const notificationData = {
    title,
    body,
    level, // info, success, warning, urgent
    recipients, // array of user uids or emails (consumer-defined)
    related: related || null,
    readBy: [],
    dismissedBy: [],
    createdAt: serverTimestamp(),
  };
  
  await setDoc(doc(db, collectionPath('notifications'), notificationId), notificationData);
  return { id: notificationId };
}

// Get a user's manager based on managerId field
async function getUserManager(userId) {
  try {
    const userDoc = await getDoc(doc(db, collectionPath('users'), userId));
    if (!userDoc.exists()) return null;
    
    const userData = userDoc.data();
    if (!userData.managerId) return null;
    
    // Fetch manager document
    const managerDoc = await getDoc(doc(db, collectionPath('users'), userData.managerId));
    if (!managerDoc.exists()) return null;
    
    return { id: managerDoc.id, ...managerDoc.data() };
  } catch (err) {
    console.error('Failed to get user manager:', err);
    return null;
  }
}

// Notify all users with role 'manager' about an opportunity extension
export async function notifyManagersAboutOpportunityExtension({ opportunity, byUser, oldDueDate, newDueDate, extraMessage = '' }) {
  try {
    // First, try to notify the user's direct manager
    const directManager = await getUserManager(byUser.uid || byUser.id);
    const recipients = [];
    const emailRecipients = [];
    
    if (directManager) {
      recipients.push(directManager.id);
      if (directManager.email) {
        emailRecipients.push({ email: directManager.email, name: directManager.displayName || directManager.email });
      }
    } else {
      // Fallback: notify all users with role 'manager' if no direct manager assigned
      const usersQ = query(collection(db, collectionPath('users')), where('role', '==', 'manager'));
      const snap = await getDocs(usersQ);
      const managers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      recipients.push(...managers.map(m => m.uid || m.id).filter(Boolean));
      emailRecipients.push(...managers.filter(m => m.email).map(m => ({ email: m.email, name: m.displayName || m.email })));
    }

    const title = `Opportunity due date extended: ${opportunity.customerName || ''}`;
    const body = `${byUser.displayName || byUser.email || 'A user'} extended the due date for opportunity ${opportunity.opportunityNumber || ''} from ${oldDueDate} to ${newDueDate}. ${extraMessage}`;

    // Create in-app notification with 'warning' level (shows in Important tab)
    await createNotification({ 
      title, 
      body, 
      level: 'warning', 
      recipients, 
      related: { type: 'Opportunity', id: opportunity.id } 
    });

    // Send email to managers (best-effort, non-blocking)
    const emailPromises = emailRecipients.map(async (recipient) => {
      try {
        const emailBody = `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #4F46E5;">Opportunity Due Date Extended</h2>
            <p>Hello ${recipient.name},</p>
            <p>${body}</p>
            <div style="margin: 20px 0; padding: 15px; background-color: #F3F4F6; border-left: 4px solid #4F46E5;">
              <strong>Extended by:</strong> ${byUser.displayName || byUser.email}<br/>
              <strong>Opportunity:</strong> ${opportunity.customerName || 'Unknown'} (#${opportunity.opportunityNumber || 'N/A'})<br/>
              <strong>Old Due Date:</strong> ${oldDueDate}<br/>
              <strong>New Due Date:</strong> ${newDueDate}
            </div>
            <a href="https://apparelcrm.itexpertsllc.com/opportunity/${opportunity.id}" 
               style="background: #4F46E5; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block; margin-top: 20px;">
              View Opportunity
            </a>
          </div>
        `;
        
        // Get API URL from environment (localhost:4000 or production server)
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
        
        if (!API_URL) {
          console.warn('No API_URL configured - email notification skipped');
          return;
        }
        
        // Use SendGrid via Express server endpoint
        const response = await fetch(`${API_URL}/api/send-notification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: recipient.email,
            subject: title,
            html: emailBody,
            fromName: 'Sofibella CRM Notifications'
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.warn(`Email to ${recipient.email} failed:`, errorData);
        } else {
          console.log(`✅ Email sent to ${recipient.email}`);
        }
      } catch (e) {
        // swallow — email failure shouldn't block client
        console.warn('Failed to send manager email notification', e);
      }
    });
    
    // Don't await - let emails send in background
    Promise.allSettled(emailPromises);
    
  } catch (err) {
    console.error('Failed to notify managers:', err);
    // Don't throw - notification failure shouldn't break the UI
  }
}

// Notify about successful import/export operations
export async function notifyImportExportComplete({ type, entityType, counts, userId }) {
  const title = `${type} completed: ${entityType}`;
  const body = `${counts.added || 0} added, ${counts.updated || 0} updated${counts.deleted ? `, ${counts.deleted} deleted` : ''}`;
  
  await createNotification({
    title,
    body,
    level: 'success',
    recipients: [userId],
    related: { type: 'Import', entityType }
  });
}

// Notify about upcoming opportunity deadlines
export async function notifyOpportunityDeadline({ opportunity, daysUntilDue, userId }) {
  let level = 'warning';
  if (daysUntilDue <= 1) level = 'urgent';
  
  const title = `Opportunity due ${daysUntilDue === 0 ? 'today' : `in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`}`;
  const body = `${opportunity.customerName || 'Unknown Customer'} - ${opportunity.opportunityNumber || ''}`;
  
  await createNotification({
    title,
    body,
    level,
    recipients: [userId],
    related: { type: 'Opportunity', id: opportunity.id }
  });
}

// Notify about upcoming task deadlines
export async function notifyTaskDeadline({ task, daysUntilDue, userId }) {
  let level = 'warning';
  if (daysUntilDue <= 1) level = 'urgent';
  
  const title = `Task due ${daysUntilDue === 0 ? 'today' : `in ${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`}`;
  const body = `${task.title || 'Untitled Task'}`;
  
  await createNotification({
    title,
    body,
    level,
    recipients: [userId],
    related: { type: 'Task', id: task.id }
  });
}

const notificationHelpers = { 
  createNotification, 
  notifyManagersAboutOpportunityExtension,
  notifyImportExportComplete,
  notifyOpportunityDeadline,
  notifyTaskDeadline,
  getUserManager
};

